import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchEmail, updateEmail, replyEmail, fetchEmailAnalysis, fetchEmailTasks, triggerEmailAnalysis, AIEmailAnalysis, AITask } from '../services/api';
import { formatRelativeTime } from '../utils/timezone';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PaperAirplaneIcon,
  TagIcon,
  CpuChipIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const EmailDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyContent, setReplyContent] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  const { data: email, isLoading } = useQuery(
    ['email', id],
    () => fetchEmail(Number(id)),
    { enabled: !!id }
  );

  // 查询AI分析结果
  const { data: aiAnalysis, isLoading: isAnalysisLoading } = useQuery(
    ['emailAnalysis', id],
    () => fetchEmailAnalysis(Number(id)),
    { 
      enabled: !!id,
      retry: false, // 如果没有分析结果就不重试
      onError: () => {
        // 静默处理错误，因为可能还没有AI分析结果
      }
    }
  );

  // 查询AI任务
  const { data: aiTasks, isLoading: isTasksLoading } = useQuery(
    ['emailTasks', id],
    () => fetchEmailTasks(Number(id)),
    { 
      enabled: !!id,
      retry: false,
      onError: () => {
        // 静默处理错误
      }
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: { status?: string; tags?: string; priority?: number } }) =>
      updateEmail(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['email', id]);
        queryClient.invalidateQueries(['emails']);
        toast.success('邮件更新成功');
      },
      onError: () => {
        toast.error('邮件更新失败');
      },
    }
  );

  const replyMutation = useMutation(
    ({ emailId, content }: { emailId: number; content: string }) => 
      replyEmail(emailId, content),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['email', id]);
        queryClient.invalidateQueries(['emails']);
        setReplyContent('');
        setShowReplyForm(false);
        toast.success('邮件回复成功');
      },
      onError: () => {
        toast.error('邮件回复失败');
      },
    }
  );

  const triggerAnalysisMutation = useMutation(
    (emailId: number) => triggerEmailAnalysis(emailId),
    {
      onSuccess: () => {
        toast.success('AI分析已完成');
        // 延迟刷新数据，给后端时间处理
        setTimeout(() => {
          queryClient.invalidateQueries(['emailAnalysis', id]);
          queryClient.invalidateQueries(['emailTasks', id]);
        }, 2000); // 等待2秒后刷新
      },
      onError: () => {
        toast.error('创建AI分析任务失败');
      },
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">邮件不存在</p>
      </div>
    );
  }

  const handleStatusUpdate = () => {
    if (selectedStatus && selectedStatus !== email.status) {
      updateMutation.mutate({ id: email.id, data: { status: selectedStatus } });
    }
  };

  const handleReply = () => {
    if (replyContent.trim()) {
      replyMutation.mutate({ emailId: email.id, content: replyContent });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap = {
      unread: { label: '未读', class: 'bg-yellow-100 text-yellow-800' },
      read: { label: '已读', class: 'bg-green-100 text-green-800' },
    };
    
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, class: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
        <div className="space-y-4">
          {/* 顶部操作栏 */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/emails')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              返回邮件列表
            </button>
            
            <div className="flex items-center space-x-3">
              {/* AI分析按钮 */}
              <button
                onClick={() => triggerAnalysisMutation.mutate(Number(id))}
                disabled={triggerAnalysisMutation.isLoading}
                className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm"
              >
                <CpuChipIcon className="h-4 w-4 mr-2" />
                {triggerAnalysisMutation.isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    AI分析中...
                  </>
                ) : (
                  'AI分析'
                )}
              </button>
              
              <select
                value={selectedStatus || email.status}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="unread">未读</option>
                <option value="read">已读</option>
              </select>
              
              <button
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || selectedStatus === email.status}
                className="disabled:opacity-50 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                更新状态
              </button>
            </div>
          </div>

          {/* 邮件详情 */}
          <div className="bg-white shadow rounded-lg p-4">
            <div className="border-b border-gray-200 pb-3 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h1 className="text-xl font-bold text-gray-900">
                  {email.subject || '(无主题)'}
                </h1>
                {getStatusBadge(email.status)}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">发件人:</span>
                  <span className="ml-2 text-gray-900">
                    {email.sender_name ? `${email.sender_name} <${email.sender_email}>` : email.sender_email}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">收件人:</span>
                  <span className="ml-2 text-gray-900">{email.recipient_email}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">接收时间:</span>
                  <span className="ml-2 text-gray-900">
                    {formatRelativeTime(email.received_at)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">分类:</span>
                  <span className="ml-2 text-gray-900">
                    <TagIcon className="inline h-4 w-4 mr-1" />
                    {email.category}
                  </span>
                </div>
              </div>
            </div>

            {/* 邮件内容 */}
            <div className="space-y-4">
              {email.summary && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">邮件摘要</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-blue-800 text-sm">{email.summary}</p>
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-base font-medium text-gray-900 mb-2">原始内容</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                  <pre className="whitespace-pre-wrap text-sm text-gray-900 font-mono">
                    {email.content}
                  </pre>
                </div>
              </div>

              {email.translated_content && email.translated_content !== email.content && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">翻译内容</h3>
                  <div className="bg-green-50 border border-green-200 rounded-md p-3">
                    <p className="text-green-800 text-sm">{email.translated_content}</p>
                  </div>
                </div>
              )}

              {email.auto_reply_content && (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">建议回复内容</h3>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <p className="text-yellow-800 text-sm">{email.auto_reply_content}</p>
                    <button
                      onClick={() => {
                        setReplyContent(email.auto_reply_content || '');
                        setShowReplyForm(true);
                      }}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      使用此内容回复
                    </button>
                  </div>
                </div>
              )}

              {/* AI分析结果 */}
              <div>
                <div className="flex items-center mb-3">
                  <CpuChipIcon className="h-5 w-5 text-purple-600 mr-2" />
                  <h3 className="text-base font-medium text-gray-900">邮件智能分析助手</h3>
                  {isAnalysisLoading && (
                    <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  )}
                </div>

                {aiAnalysis ? (
                  <div className="space-y-4">
                    {/* 中文翻译 */}
                    {(aiAnalysis.chinese_translation || aiAnalysis.translated_content) && 
                     (aiAnalysis.chinese_translation || aiAnalysis.translated_content) !== email.content && (
                      <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center text-sm">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          AI中文翻译
                        </h4>
                        <p className="text-blue-800 text-sm">
                          {aiAnalysis.chinese_translation || aiAnalysis.translated_content}
                        </p>
                      </div>
                    )}

                    {/* 中文摘要 */}
                    {(aiAnalysis.chinese_summary || aiAnalysis.summary) && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <h4 className="font-medium text-green-900 mb-2 flex items-center text-sm">
                          <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                          AI智能摘要
                        </h4>
                        <p className="text-green-800 text-sm">
                          {aiAnalysis.chinese_summary || aiAnalysis.summary}
                        </p>
                      </div>
                    )}

                    {/* 情感分析和分类 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {aiAnalysis.sentiment && (
                        <div className="bg-pink-50 border border-pink-200 rounded-md p-3">
                          <h4 className="font-medium text-pink-900 mb-2 flex items-center text-sm">
                            <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                            情感分析
                          </h4>
                          <p className="text-pink-800 font-medium text-sm">{aiAnalysis.sentiment}</p>
                        </div>
                      )}

                      {aiAnalysis.category && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
                          <h4 className="font-medium text-indigo-900 mb-2 flex items-center text-sm">
                            <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                            AI分类
                          </h4>
                          <p className="text-indigo-800 font-medium text-sm">{aiAnalysis.category}</p>
                        </div>
                      )}

                      {aiAnalysis.priority_score !== undefined && (
                        <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                          <h4 className="font-medium text-orange-900 mb-2 flex items-center text-sm">
                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                            优先级评分
                          </h4>
                          <div className="flex items-center">
                            <span className="text-orange-800 font-bold text-base">{aiAnalysis.priority_score}</span>
                            <span className="text-orange-600 text-xs ml-1">/10</span>
                            <div className="ml-2 flex-1 bg-orange-200 rounded-full h-1.5">
                              <div 
                                className="bg-orange-500 h-1.5 rounded-full" 
                                style={{ width: `${(aiAnalysis.priority_score / 10) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* AI标签 */}
                    {aiAnalysis.tags && aiAnalysis.tags.length > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center text-sm">
                          <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                          AI智能标签
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {aiAnalysis.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                            >
                              <TagIcon className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* AI建议回复 */}
                    {aiAnalysis.suggested_reply && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <h4 className="font-medium text-yellow-900 mb-2 flex items-center text-sm">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                          AI建议回复
                        </h4>
                        
                        {/* 英文原版回复 */}
                        <div className="bg-white border border-yellow-300 rounded-md p-2 mb-2">
                          <h5 className="font-medium text-yellow-900 mb-1 text-xs">英文版本：</h5>
                          <pre className="text-yellow-800 text-xs whitespace-pre-wrap font-mono">
                            {aiAnalysis.suggested_reply}
                          </pre>
                        </div>
                        
                        {/* 中文翻译的回复 */}
                        {aiAnalysis.reply_chinese_translation && (
                          <div className="bg-yellow-100 border border-yellow-300 rounded-md p-2 mb-2">
                            <h5 className="font-medium text-yellow-900 mb-1 text-xs">中文翻译：</h5>
                            <p className="text-yellow-800 text-xs">
                              {aiAnalysis.reply_chinese_translation}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setReplyContent(aiAnalysis.suggested_reply || '');
                              setShowReplyForm(true);
                            }}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                          >
                            使用英文回复
                          </button>
                          
                          {aiAnalysis.reply_chinese_translation && (
                            <button
                              onClick={() => {
                                setReplyContent(aiAnalysis.reply_chinese_translation || '');
                                setShowReplyForm(true);
                              }}
                              className="px-2 py-1 text-xs bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                            >
                              使用中文回复
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* 置信度分数 */}
                    {aiAnalysis.confidence_scores && Object.keys(aiAnalysis.confidence_scores).length > 0 && (
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                        <h4 className="font-medium text-slate-900 mb-3 flex items-center">
                          <span className="w-2 h-2 bg-slate-500 rounded-full mr-2"></span>
                          AI分析置信度
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(aiAnalysis.confidence_scores).map(([key, value]) => {
                            const keyMap: Record<string, string> = {
                              'translation': '翻译',
                              'summary': '摘要',
                              'tags': '标签',
                              'reply': '回复',
                              'category': '分类',
                              'sentiment': '情感',
                              'priority': '优先级'
                            };
                            const displayKey = keyMap[key] || key;
                            const percentage = (value * 100);
                            
                            return (
                              <div key={key} className="bg-white border border-slate-200 rounded-md p-3">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-sm text-slate-700 font-medium">{displayKey}</span>
                                  <span className="text-sm font-bold text-slate-900">
                                    {percentage.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      percentage >= 80 ? 'bg-green-500' :
                                      percentage >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 分析时间 */}
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">分析时间：</span>
                        {(() => {
                      let date: Date;
                      if (aiAnalysis.created_at.includes('T') && !aiAnalysis.created_at.includes('Z') && !aiAnalysis.created_at.includes('+')) {
                        date = new Date(aiAnalysis.created_at + 'Z');
                      } else {
                        date = new Date(aiAnalysis.created_at);
                      }
                      return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                    })()}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-6 text-center">
                    <CpuChipIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-3">暂无AI分析结果</p>
                    <p className="text-sm text-gray-500 mb-4">
                      点击上方"重新分析"按钮，让AI助手为这封邮件生成智能分析
                    </p>
                    <button
                      onClick={() => triggerAnalysisMutation.mutate(Number(id))}
                      disabled={triggerAnalysisMutation.isLoading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                    >
                      {triggerAnalysisMutation.isLoading ? '分析中...' : '开始AI分析'}
                    </button>
                  </div>
                )}

                {/* AI任务状态 - 开发调试信息 */}
                {aiTasks && aiTasks.length > 0 && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800 font-medium">
                      🔧 开发调试信息 - AI任务状态 ({aiTasks.length}个任务)
                    </summary>
                    <div className="mt-3 space-y-2">
                      {aiTasks.map((task) => (
                        <div
                          key={task.id}
                          className={`border rounded-md p-3 text-sm ${
                            task.status === 'COMPLETED'
                              ? 'bg-green-50 border-green-200'
                              : task.status === 'FAILED'
                              ? 'bg-red-50 border-red-200'
                              : task.status === 'RUNNING'
                              ? 'bg-blue-50 border-blue-200'
                              : 'bg-yellow-50 border-yellow-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">
                              任务 #{task.id} - {task.task_type}
                            </span>
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                task.status === 'COMPLETED'
                                  ? 'bg-green-100 text-green-800'
                                  : task.status === 'FAILED'
                                  ? 'bg-red-100 text-red-800'
                                  : task.status === 'RUNNING'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {task.status}
                            </span>
                          </div>
                          
                          {task.error_message && (
                            <div className="flex items-start mt-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-red-700 text-xs">{task.error_message}</p>
                            </div>
                          )}
                          
                          <div className="mt-2 text-xs text-gray-600">
                                                              <p>创建时间: {(() => {
                  let date: Date;
                  if (task.created_at.includes('T') && !task.created_at.includes('Z') && !task.created_at.includes('+')) {
                    date = new Date(task.created_at + 'Z');
                  } else {
                    date = new Date(task.created_at);
                  }
                  return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                })()}</p>
                {task.completed_at && (
                  <p>完成时间: {(() => {
                    let date: Date;
                    if (task.completed_at.includes('T') && !task.completed_at.includes('Z') && !task.completed_at.includes('+')) {
                      date = new Date(task.completed_at + 'Z');
                    } else {
                      date = new Date(task.completed_at);
                    }
                    return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
                  })()}</p>
                )}
                          </div>

                          {/* Debug信息 */}
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                              查看详细数据
                            </summary>
                            <div className="mt-2 p-2 bg-gray-100 rounded text-xs text-gray-900 dark:text-gray-900">
                              <div className="mb-2">
                                <strong>输入数据:</strong>
                                <pre className="mt-1 whitespace-pre-wrap">
                                  {JSON.stringify(task.input_data, null, 2)}
                                </pre>
                              </div>
                              <div>
                                <strong>输出数据:</strong>
                                <pre className="mt-1 whitespace-pre-wrap">
                                  {JSON.stringify(task.output_data, null, 2)}
                                </pre>
                              </div>
                            </div>
                          </details>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          </div>

          {/* 回复区域 */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">回复邮件</h3>
              {!showReplyForm && (
                <button
                  onClick={() => setShowReplyForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  编写回复
                </button>
              )}
            </div>

            {showReplyForm && (
              <div className="space-y-4">
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="请输入回复内容..."
                  rows={8}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleReply}
                    disabled={!replyContent.trim() || replyMutation.isLoading}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    {replyMutation.isLoading ? '发送中...' : '发送回复'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowReplyForm(false);
                      setReplyContent('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    取消
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailDetail; 